import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Check, Loader2, Shield, LayoutGrid, Sparkles } from 'lucide-react';
import { Profile } from '../../types';
import { api } from '../../src/services/api';
import { useToast } from '../../src/contexts/ToastContext';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { useAuth } from '../../src/contexts/AuthContext';
import { APROVACAO_DESCONTO_PERMISSION } from '../../src/components/AprovacaoDescontoPopup';
import { GERAR_ROTA_TERCEIROS_PERMISSION } from '../CRM/Rotas/rotaTypes';
import { TRANSFERIR_NEGOCIACOES_PERMISSION } from '../../src/lib/transferenciaNegociacao';
import { SUPERVISIONAR_COMERCIAL_PERMISSION } from '../../src/lib/supervisaoComercial';
import { PAGAR_RECOMPENSAS_PERMISSION, TRABALHAR_INDICACOES_PERMISSION } from '../../src/lib/indicacoes/permissoes';
import { VALIDAR_KPI_PERMISSION } from '../../src/lib/dashboard/kpisDepartamento';
import { EDITAR_CADASTRO_PERMISSION } from '../../src/lib/permissaoCadastro';

interface PermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userProfile: Profile;
  onSuccess: (updatedProfile: Profile) => void;
}

interface PermissionItem {
  id: string;
  label: string;
  description: string;
  /** Só listada se o usuário tiver este módulo (por papel ou concessão). */
  requiresModule?: string;
}

const SPECIAL_PERMISSIONS: PermissionItem[] = [
  {
    id: 'Receber Cobranças pendentes / Faturamento',
    label: 'Receber Cobranças pendentes / Faturamento',
    description: 'Permite que o usuário receba a distribuição de cobranças pendentes na aba de Janela de Faturamento.'
  },
  {
    id: 'Auditoria de Pagamentos',
    label: 'Auditoria de Pagamentos',
    description: 'Permite acessar a tela de Auditoria de Pagamentos (Faturas e Cobranças), onde boletos pagos — inclusive de empresas parceiras e boleto único — são conferidos, têm o repasse à Equatorial acompanhado e são marcados como auditados. Vale para usuários com papel Administrador ou Analista.'
  },
  {
    id: 'Auditoria Boleto Único',
    label: 'Auditoria Boleto Único',
    description: 'Dá acesso à mesma tela de Auditoria de Pagamentos (as duas telas foram unificadas). Mantida como permissão separada só para não exigir reconcessão de quem já a tinha.'
  },
  {
    id: 'Receber aviso de inadimplência',
    label: 'Receber aviso de inadimplência',
    description: 'Recebe notificação no VigorHub quando uma cobrança completa os 3 avisos ao consumidor (D-1, D+3, D+10) e atinge 15 dias de atraso sem pagamento.'
  },
  {
    id: 'Realizar Cobrança / Negociação',
    label: 'Realizar Cobrança / Negociação',
    description: 'Permite receber clientes na carteira de cobrança pós-envio (aba "Cobrança enviada"): negociar status, agendar follow-up e transferir clientes entre analistas.'
  },
  {
    id: 'Receber alarme das geradoras',
    label: 'Receber alarme das geradoras',
    description: 'Recebe notificação no VigorHub quando um inversor reporta falha (ventilador, isolamento, sobretensão, tensão da rede fora do limite). Concessão nominal: sem esta permissão ninguém é avisado, mesmo tendo acesso ao módulo.',
    // Só aparece para quem consegue abrir a tela de Geradoras. Avisar quem não
    // tem o módulo geraria notificação com link que ele não pode acessar.
    requiresModule: 'modulo:geradoras',
  },
  {
    id: APROVACAO_DESCONTO_PERMISSION,
    label: 'Aprovar desconto fora da política',
    description: 'Abre o pop-up de aprovação ao entrar no sistema, listando as negociações com desconto fora dos planos oficiais (ou com carência) à espera de decisão. Sem esta permissão o usuário não recebe a fila — o botão Aprovar/Rejeitar no feed da negociação continua valendo pelo papel.'
  },
  {
    id: GERAR_ROTA_TERCEIROS_PERMISSION,
    label: 'Fazer Rotas (gerar para outros vendedores)',
    description: 'Libera, em CRM > Rotas, o seletor de vendedores e a geração/roteirização de rota em nome de outras pessoas — o mesmo que adm/gerente já fazem lá. Não abre as abas de gestão (Produtividade, Capturas, Aderência, Operacional), que continuam restritas a esses papéis. Também libera o acesso à Prospecção Geo (CRM > Prospecção Geo), que busca empresas por região via API paga (Google Places) — conceda com atenção ao custo.'
  },
  {
    id: TRANSFERIR_NEGOCIACOES_PERMISSION,
    label: 'Transferir negociações',
    description: 'Permite trocar o responsável de uma negociação no CRM: pelo campo "Responsável" dentro da negociação e pela seleção em massa na visão de tabela — o mesmo que adm/gerente já fazem. O novo responsável é avisado e o SLA do anterior é encerrado como transferência.'
  },
  {
    id: SUPERVISIONAR_COMERCIAL_PERMISSION,
    label: 'Supervisionar o Comercial',
    description: 'Para quem acompanha Closers e BDRs (o Hunter, por exemplo). No CRM e no Analytics Comercial, a pessoa passa a ver as próprias negociações e as das Closers e BDRs, vê o SLA de atendimento delas e transfere negociação entre elas. Aprovar desconto e mexer em escala continuam com a gerente.'
  },
  {
    id: TRABALHAR_INDICACOES_PERMISSION,
    label: 'Trabalhar indicações',
    description: 'Libera o Programa de Indicações completo (CRM > Indicações): vê todos os clientes e todas as abordagens e indicações do time, e registra abordagem para qualquer cliente. É a permissão do pós-venda — Closers, BDRs, gerente e administrador já têm isso pela função ou pelo papel. Sem ela, a pessoa só vê o que ela mesma registrou.'
  },
  {
    id: PAGAR_RECOMPENSAS_PERMISSION,
    label: 'Pagar recompensas de indicação',
    description: 'Abre a aba Recompensas em CRM > Indicações: vê a fila de recompensas, ajusta tipo e valor antes de pagar, marca como paga (com comprovante) e cancela. Gerente e administrador já têm isso pelo papel.'
  },
  {
    id: EDITAR_CADASTRO_PERMISSION,
    label: 'Editar dados cadastrais',
    description: 'Libera a alteração dos dados cadastrais de UC em operação: nome, CPF/CNPJ e nascimento do cliente e do titular, endereço, contatos, número da UC e o pagador do boleto. Sem ela, esses campos ficam travados (o banco também recusa) e a pessoa pede a correção ao Financeiro. É do Financeiro; administrador já tem pelo papel. UC ainda na venda, antes da validação de dados, continua livre.'
  },
  {
    id: VALIDAR_KPI_PERMISSION,
    label: 'Validar KPIs dos departamentos',
    description: 'Mostra os botões Validar e Recusar nos KPIs que os gestores propõem na aba CEO do Painel Executivo. O banco recusa a validação de quem não tem esta permissão. É do CEO.'
  }
];

const MODULE_PERMISSIONS = [
  {
    id: 'modulo:geradoras',
    label: 'Geradoras',
    description: 'Acesso ao módulo de monitoramento de geradoras fotovoltaicas.'
  },
  {
    id: 'modulo:marketing',
    label: 'Marketing',
    description: 'Acesso ao módulo de campanhas e criativos de marketing.'
  },
  {
    id: 'modulo:gestao',
    label: 'Gestão',
    description: 'Acesso ao módulo de gestão e análise operacional.'
  }
];

// Estas permissões liberam o número AGREGADO da empresa — a carteira inteira, a
// inadimplência total, o funil fechado. Poder abrir a tela do módulo já basta
// para perguntar sobre um caso individual (um cliente, uma UC, uma usina); o
// total pronto numa frase é outra coisa, e por isso é concedido um a um.
//
// Valem para as DUAS pontas: a resposta que a Vick dá quando perguntada e o
// alerta que o vigia manda sem ser perguntado. Eram duas réguas até 31/08/2026
// — o alerta seguia o papel, e analista sem permissão nenhuma recebia o
// Financeiro da empresa no sininho com as caixas daqui desmarcadas.
// A régua vive em `supabase/functions/_shared/vickCatalogo/tipos.ts` (resposta)
// e em `public.ia_alerta_visivel` / `destinatarios_ia_alerta` (alerta).
const VICK_DATA_PERMISSIONS = [
  {
    id: 'vick:financeiro',
    label: 'Financeiro geral',
    description: 'Permite que a Vick responda com números agregados do Financeiro (carteira do mês, inadimplência total, previsão de recebimento) e que a pessoa receba os alertas do vigia deste módulo. Role "adm" já tem isso por padrão.'
  },
  {
    id: 'vick:rateio',
    label: 'Rateio e energia (agregado)',
    description: 'Permite que a Vick responda com o panorama de saldo e consumo da carteira inteira e envie os alertas de energia (saldo baixo, variação de consumo). Sem isto, ela ainda responde sobre uma UC específica para quem tem acesso ao módulo.'
  },
  {
    id: 'vick:comercial',
    label: 'Comercial geral',
    description: 'Permite que a Vick responda com o funil fechado e as conversões da empresa e envie os alertas comerciais. Sem isto, ela ainda responde sobre um vendedor específico.'
  },
  {
    id: 'vick:geradoras',
    label: 'Geradoras (agregado)',
    description: 'Permite que a Vick responda com números consolidados de geração e envie os alertas de geradora parada. Sem isto, ela ainda responde sobre uma usina específica para quem tem acesso ao módulo. Não substitui "Receber alarme das geradoras", que é o alarme do inversor.'
  }
];

// Modules already accessible by default per role (cannot be revoked here — controlled by role)
const ROLE_DEFAULT_MODULES: Record<string, string[]> = {
  adm: ['modulo:geradoras', 'modulo:marketing', 'modulo:gestao'],
  gerente: ['modulo:geradoras', 'modulo:marketing'],
  analista: ['modulo:marketing', 'modulo:gestao'],
  vendedor: [],
  parceiro: [],
  vendedor_parceiro: [],
  gerente_parcerias: [],
  motoboy: [],
  empresa_parceira_admin: [],
  empresa_parceira_operador: [],
};

// "adm" já alcança todo o catálogo da Vick por padrão (não pode ser revogado aqui)
const ROLE_DEFAULT_VICK: Record<string, string[]> = {
  adm: ['vick:financeiro', 'vick:rateio', 'vick:comercial', 'vick:geradoras'],
};

const PermissionsModal: React.FC<PermissionsModalProps> = ({
  isOpen,
  onClose,
  userProfile,
  onSuccess
}) => {
  const { profile: currentUserProfile } = useAuth();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(
    userProfile.permissions || []
  );

  if (!isOpen) return null;

  const defaultModules = ROLE_DEFAULT_MODULES[userProfile.role] || [];
  const defaultVick = ROLE_DEFAULT_VICK[userProfile.role] || [];

  // Lê de selectedPermissions, não do perfil salvo: marcar o módulo faz a
  // permissão dependente aparecer na hora, sem precisar salvar e reabrir.
  const temModulo = (moduloId: string) =>
    defaultModules.includes(moduloId) || selectedPermissions.includes(moduloId);

  const handleTogglePermission = (permissionId: string) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(p => p !== permissionId);
      }
      return [...prev, permissionId];
    });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Tirar o módulo tem que tirar junto as permissões que dependem dele.
      // Senão a concessão fica salva e invisível: some da tela, mas continua no
      // array e volta a valer sozinha se o módulo for devolvido depois.
      const dependentesOrfas = SPECIAL_PERMISSIONS
        .filter(p => p.requiresModule && !temModulo(p.requiresModule))
        .map(p => p.id);
      const permissoesFinais = selectedPermissions.filter(p => !dependentesOrfas.includes(p));

      const updatedProfile = await api.auth.updateProfile(userProfile.id, {
        permissions: permissoesFinais
      });

      await logAction(currentUserProfile?.id, 'UPDATE_USER_PERMISSIONS', {
        target_user_id: userProfile.id,
        new_permissions: permissoesFinais
      });
      logUsageEvent({ userId: currentUserProfile?.id, modulo: 'config', funcionalidade: 'usuarios', acao: 'atualizar_permissoes_usuario', origem: 'acao' });

      addToast('Permissões atualizadas com sucesso.', 'success');
      onSuccess(updatedProfile);
      onClose();
    } catch (error: any) {
      console.error('Error updating permissions:', error);
      addToast(error.message || 'Erro ao atualizar permissões.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderToggleItem = (permission: { id: string; label: string; description: string }) => {
    const isSelected = selectedPermissions.includes(permission.id);
    return (
      <div
        key={permission.id}
        onClick={() => handleTogglePermission(permission.id)}
        className={`relative flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
          isSelected
            ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/10'
            : 'border-gray-200 dark:border-gray-700 hover:border-indigo-200 dark:hover:border-indigo-800'
        }`}
      >
        <div className={`mt-0.5 shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-colors ${
          isSelected
            ? 'bg-indigo-600 border-indigo-600 text-white'
            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
        }`}>
          {isSelected && <Check size={14} strokeWidth={3} />}
        </div>
        <div>
          <h3 className={`font-bold text-sm ${isSelected ? 'text-indigo-900 dark:text-indigo-100' : 'text-gray-900 dark:text-white'}`}>
            {permission.label}
          </h3>
          <p className={`text-xs mt-1 ${isSelected ? 'text-indigo-700/80 dark:text-indigo-300' : 'text-gray-500 dark:text-gray-400'}`}>
            {permission.description}
          </p>
        </div>
      </div>
    );
  };

  const renderDefaultModuleItem = (permission: { id: string; label: string; description: string }) => (
    <div
      key={permission.id}
      className="flex items-start gap-4 p-4 rounded-xl border-2 border-gray-100 dark:border-gray-700/50 bg-gray-50/60 dark:bg-gray-700/20"
    >
      <div className="mt-0.5 shrink-0 w-5 h-5 rounded border border-emerald-400 dark:border-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
        <Check size={14} strokeWidth={3} className="text-emerald-600 dark:text-emerald-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-bold text-sm text-gray-500 dark:text-gray-400">{permission.label}</h3>
          <span className="text-[10px] font-semibold px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 rounded-full">
            Incluso no papel
          </span>
        </div>
        <p className="text-xs mt-1 text-gray-400 dark:text-gray-500">{permission.description}</p>
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 animate-fade-in">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg">
              <Shield size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white font-poppins">Permissões Especiais</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Gerenciar acessos de {userProfile.full_name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh] space-y-6">
          {/* Special Permissions */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Shield size={15} className="text-indigo-500 dark:text-indigo-400" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Permissões Especiais</span>
            </div>
            <div className="space-y-3">
              {SPECIAL_PERMISSIONS
                .filter(p => !p.requiresModule || temModulo(p.requiresModule))
                .map(renderToggleItem)}
            </div>
          </div>

          {/* Module Access */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <LayoutGrid size={15} className="text-indigo-500 dark:text-indigo-400" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Acesso a Módulos</span>
            </div>
            <div className="space-y-3">
              {MODULE_PERMISSIONS.map(permission =>
                defaultModules.includes(permission.id)
                  ? renderDefaultModuleItem(permission)
                  : renderToggleItem(permission)
              )}
            </div>
          </div>

          {/* Vick — Acesso a Dados */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={15} className="text-indigo-500 dark:text-indigo-400" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Vick — Acesso a Dados</span>
            </div>
            <div className="space-y-3">
              {VICK_DATA_PERMISSIONS.map(permission =>
                defaultVick.includes(permission.id)
                  ? renderDefaultModuleItem(permission)
                  : renderToggleItem(permission)
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-all shadow-md shadow-indigo-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} />}
            Salvar Permissões
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PermissionsModal;
